import { Base64 } from "js-base64"
import { ArrowDownToLine, ChevronLeft, Circle, Info, Loader2, OctagonX, RotateCcw, Square, Terminal } from "lucide-react"
import { useEffect, useId, useRef, useState } from "react"
import type { ComputerSummary } from "../../computers/types"
import type { NestClient } from "../../nest/client"
import type { RuntimeRecord } from "../../nest/generated"
import { ProcessPortLink } from "./ProcessPortLink"
import { ProcessTaskLink, type ProcessTaskNavigation } from "./ProcessTaskLink"

export const processButtonClass =
    "btn flex h-7 w-7 shrink-0 items-center justify-center text-muted transition-colors hover:bg-base-200 hover:text-base-content focus-visible:outline focus-visible:outline-primary disabled:opacity-40 max-sm:h-9 max-sm:w-9"
export const isActive = (process: RuntimeRecord) => process.status === "running" || process.status === "starting"

export function ProcessStatus({ process, compact = false }: { process: RuntimeRecord; compact?: boolean }) {
    const color =
        process.status === "failed" ? "text-error" : process.status === "starting" ? "text-warning" : isActive(process) ? "text-success" : "text-muted"
    return (
        <span className={`flex shrink-0 items-center gap-1.5 text-[10px] ${color}`} title={process.status}>
            <Circle size={6} fill="currentColor" aria-hidden="true" />
            <span className={compact ? "sr-only" : undefined}>{process.status}</span>
        </span>
    )
}

export function ProcessDetails({
    client,
    computer,
    initial,
    onChange,
    onBack,
    taskNavigation,
}: {
    client: NestClient
    computer: ComputerSummary
    initial: RuntimeRecord
    onChange(): void
    onBack(): void
    taskNavigation: ProcessTaskNavigation
}) {
    const computerName = computer.name
    const [process, setProcess] = useState(initial)
    const [error, setError] = useState("")
    const [readError, setReadError] = useState("")
    const [busy, setBusy] = useState(false)
    const [revision, setRevision] = useState(0)
    const [detailsOpen, setDetailsOpen] = useState(false)
    const detailsId = useId()
    const lifetime = useRef<AbortController | null>(null)
    const processId = initial.runtimeId
    useEffect(() => {
        const controller = new AbortController()
        lifetime.current = controller
        return () => controller.abort()
    }, [client, processId])
    useEffect(() => {
        const controller = new AbortController()
        let timer: ReturnType<typeof setTimeout> | undefined
        const refresh = async () => {
            try {
                const result = await client.requestMethod("process.read", { processId }, { signal: controller.signal, timeoutMs: 15_000 })
                if (!controller.signal.aborted) {
                    setProcess(result.process)
                    setReadError("")
                }
            } catch (err) {
                if (!controller.signal.aborted) setReadError(String(err))
            } finally {
                if (!controller.signal.aborted) timer = setTimeout(refresh, 2_000)
            }
        }
        void refresh()
        return () => {
            controller.abort()
            clearTimeout(timer)
        }
    }, [client, processId, revision])

    const control = async (action: "stop" | "kill" | "restart") => {
        const signal = lifetime.current?.signal
        if (!signal || signal.aborted) return
        setBusy(true)
        try {
            if (action === "restart") await client.requestMethod("process.restart", { processId }, { signal, timeoutMs: 15_000 })
            else {
                const result = await client.requestMethod(
                    "process.stop",
                    { processId, signal: action === "kill" ? "KILL" : "TERM" },
                    { signal, timeoutMs: 15_000 }
                )
                if (!result.stopped) throw new Error("The process has not stopped")
            }
            if (!signal.aborted) {
                setError("")
                setRevision((value) => value + 1)
                onChange()
            }
        } catch (err) {
            if (!signal.aborted) setError(String(err))
        } finally {
            if (!signal.aborted) setBusy(false)
        }
    }
    return (
        <div className="flex h-full min-h-0 min-w-0 flex-col">
            <div className="flex shrink-0 items-center gap-2 border-b border-border px-2 py-1">
                <button type="button" className={`${processButtonClass} md:hidden`} aria-label="All computers" title="Back to all computers" onClick={onBack}>
                    <ChevronLeft size={15} />
                </button>
                <div className="min-w-0 flex-1 pl-1">
                    <div className="flex items-center gap-2">
                        <span
                            className="truncate text-xs font-medium"
                            title={`${computerName} / ${process.namespace || ""} / ${process.name || process.label || processId}`}
                        >
                            {process.name || process.label || processId}
                        </span>
                        <ProcessStatus process={process} />
                    </div>
                </div>
                {busy ? <Loader2 size={12} className="shrink-0 animate-spin text-muted" aria-label="Updating process" /> : null}
                <div className="flex shrink-0 items-center gap-0.5">
                    <button
                        type="button"
                        disabled={busy || Boolean(readError) || !isActive(process)}
                        className={processButtonClass}
                        aria-label="Stop"
                        title="Stop process"
                        onClick={() => void control("stop")}
                    >
                        <Square size={13} />
                    </button>
                    <button
                        type="button"
                        disabled={busy || Boolean(readError) || !process.restartable}
                        className={processButtonClass}
                        aria-label="Restart"
                        title="Restart process"
                        onClick={() => void control("restart")}
                    >
                        <RotateCcw size={14} />
                    </button>
                    <button
                        type="button"
                        disabled={busy || Boolean(readError) || !isActive(process)}
                        className={`${processButtonClass} hover:text-error`}
                        aria-label="Kill"
                        title="Force kill process"
                        onClick={() => void control("kill")}
                    >
                        <OctagonX size={14} />
                    </button>
                    <button
                        type="button"
                        className={`${processButtonClass} ml-1 border-l border-border`}
                        aria-label="Process details"
                        title="Process details"
                        aria-expanded={detailsOpen}
                        aria-controls={detailsId}
                        onClick={() => setDetailsOpen((value) => !value)}
                    >
                        <Info size={14} />
                    </button>
                </div>
            </div>
            <div className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-1.5 text-[11px] text-muted">
                <Terminal size={12} className="shrink-0" />
                <code className="min-w-0 flex-1 truncate" title={process.commandPreview}>
                    {process.commandPreview}
                </code>
                <ProcessPortLink computer={computer} port={process.port} name={process.name || process.label || processId} />
                <span className="max-w-[30%] truncate text-[10px]" title={computerName}>
                    {computerName}
                </span>
            </div>
            {detailsOpen ? (
                <dl
                    id={detailsId}
                    className="grid max-h-48 shrink-0 grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 overflow-auto border-b border-border bg-base-200 px-3 py-2 text-[11px]"
                >
                    <dt className="text-muted">Directory</dt>
                    <dd className="break-all font-mono">{process.cwd || "—"}</dd>
                    <dt className="text-muted">Namespace</dt>
                    <dd className="break-all">{process.namespace || "—"}</dd>
                    <dt className="text-muted">Command</dt>
                    <dd className="break-all font-mono">{process.commandPreview}</dd>
                    <dt className="text-muted">Process</dt>
                    <dd className="break-all font-mono">
                        {processId}
                        {process.pid ? ` · PID ${process.pid}` : ""}
                        {process.exitCode != null ? ` · Exit ${process.exitCode}` : ""}
                    </dd>
                    {process.labels?.["wildlark.taskId"] ? (
                        <>
                            <dt className="text-muted">Started by</dt>
                            <dd className="break-words">
                                <ProcessTaskLink process={process} nodeId={computer.nodeId} navigation={taskNavigation} />
                                <span className="block break-all font-mono text-[10px] text-muted">{process.labels["wildlark.taskId"]}</span>
                            </dd>
                            {process.labels["wildlark.sessionId"] ? (
                                <>
                                    <dt className="text-muted">Session</dt>
                                    <dd className="break-all font-mono">{process.labels["wildlark.sessionId"]}</dd>
                                </>
                            ) : null}
                        </>
                    ) : null}
                </dl>
            ) : null}
            {error || readError ? (
                <p role="alert" className="shrink-0 break-words border-b border-border px-3 py-2 text-xs text-error">
                    {error || readError}
                </p>
            ) : null}
            <ProcessLogs client={client} processId={processId} />
        </div>
    )
}

function ProcessLogs({ client, processId }: { client: NestClient; processId: string }) {
    const [output, setOutput] = useState("")
    const [error, setError] = useState("")
    const [dropped, setDropped] = useState(false)
    const [following, setFollowing] = useState(true)
    const container = useRef<HTMLPreElement>(null)
    useEffect(() => {
        if (following && container.current) container.current.scrollTop = container.current.scrollHeight
    }, [output, following])
    useEffect(() => {
        const controller = new AbortController()
        let offset = 0
        let timer: ReturnType<typeof setTimeout> | undefined
        const refresh = async () => {
            try {
                const result = await client.requestMethod(
                    "process.output.read",
                    { processId, offset, maxBytes: 65536 },
                    { signal: controller.signal, timeoutMs: 15_000 }
                )
                if (controller.signal.aborted) return
                offset = result.nextOffset
                if (result.droppedBeforeOffset != null) setDropped(true)
                const text = result.chunks.map((chunk) => (chunk.encoding === "base64" ? Base64.decode(chunk.data) : chunk.data)).join("")
                if (text) setOutput((previous) => (previous + text).slice(-256 * 1024))
                setError("")
            } catch (err) {
                if (!controller.signal.aborted) setError(String(err))
            } finally {
                if (!controller.signal.aborted) timer = setTimeout(refresh, 1000)
            }
        }
        void refresh()
        return () => {
            controller.abort()
            clearTimeout(timer)
        }
    }, [client, processId])
    return (
        <>
            {error ? (
                <p role="alert" className="shrink-0 break-words px-3 py-2 text-xs text-error">
                    {error}
                </p>
            ) : null}
            <pre
                ref={container}
                role="region"
                aria-label="Process logs"
                tabIndex={0}
                onScroll={() => {
                    const el = container.current
                    if (el) setFollowing(el.scrollHeight - el.scrollTop - el.clientHeight < 50)
                }}
                className="min-h-0 flex-1 overflow-auto overscroll-contain whitespace-pre-wrap break-all bg-editor-background p-3 font-mono text-xs leading-5 outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary"
            >
                {output || <span className="text-muted">No output yet.</span>}
            </pre>
            <div className="flex shrink-0 items-center gap-2 border-t border-border px-3 py-1 text-[10px] text-muted">
                <span className="min-w-0 flex-1 truncate" title={dropped ? "Earlier output is no longer available" : undefined}>
                    {dropped ? "Earlier output unavailable" : "Output"}
                </span>
                <button
                    type="button"
                    className={`btn flex items-center gap-1 px-1 py-1 text-[10px] hover:text-base-content ${following ? "text-primary" : "text-muted"}`}
                    aria-label="Follow output"
                    aria-pressed={following}
                    title={following ? "Pause following output" : "Jump to latest output"}
                    onClick={() => setFollowing((value) => !value)}
                >
                    <ArrowDownToLine size={12} /> Follow
                </button>
            </div>
        </>
    )
}
